import { useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronDown, Folder, UserCircle, X } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  popoverBoundedMaxWidthClass,
} from '@reflex/ui/components/ui/popover';
import type { AgentPersona } from '@reflex/plugin-agent-persona/shared/types';
import type { PluginAttachmentValue } from '@/lib/types';
import type { ResolvedAttachment } from '@/lib/plugin-registry';
import { usePlugins } from '@/hooks/config/usePlugins';
import { useAttachmentPickerProviders } from '@/hooks/config/useAttachmentPickerProviders';
import {
  AttachmentPickerPanel,
  AttachmentSummaryLabel,
} from '@/components/agent/attachments/AttachmentChipParts';
import { summaryOwnsIcon } from '@/components/agent/attachments/attachment-chip-utils';
import { AgentRunBadge } from '@/components/agent/AgentRunBadge';

/** A referenced agent run shown as a removable badge in the applied-context row. */
export interface AppliedAgentRunReference {
  id: string;
  title: string;
}

export interface AppliedContextBarProps {
  /** Plugin attachments currently applied to the run, keyed by attachmentId. */
  attachmentValues: Record<string, PluginAttachmentValue>;
  /** The persona applied to the run, or `null`/`undefined` when none. */
  activePersona: AgentPersona | null | undefined;
  /**
   * The group the run will be filed into, or `null` when none. Shown here and
   * not only in the collapsed Advanced section because filing is a decision
   * about the run, and the sidebar's per-group "+" applies one before the user
   * has typed anything — an applied group nobody can see is one nobody can
   * correct.
   */
  activeGroup?: { id: string; name: string } | null;
  /** Clear the applied group. Required for the group chip to be removable. */
  onClearGroup?: () => void;
  /** Agent runs referenced via the `@`-mention menu (unrolled on submit). */
  references?: AppliedAgentRunReference[];
  /** Clear the applied persona. */
  onClearPersona: () => void;
  /** Remove an applied attachment by id. */
  onRemoveAttachment: (attachmentId: string) => void;
  /**
   * Replace an applied attachment's config. When provided, chips whose plugin
   * summary supports it can edit part of the config in place (e.g. switching
   * the repo chip's branch) instead of sending the user to the picker.
   */
  onChangeAttachment?: (attachmentId: string, config: unknown) => void;
  /** Remove a referenced agent run by id. */
  onRemoveReference?: (agentId: string) => void;
  /**
   * Write an attachment's config from the chip's own picker, or `null` to clear
   * it. Providing this is what gives attachment chips their disclosure control,
   * so a multi-value attachment (MCP servers, AI gateways) can be edited one
   * item at a time instead of only cleared wholesale.
   *
   * Deliberately separate from {@link onChangeAttachment}: that one is the
   * summary's in-place edit and must never resurrect an attachment the user
   * just removed, while this one legitimately re-adds after the last item is
   * deselected. It carries `pluginName` for the same reason
   * `AgentAttachmentsEditor` does: the entry may not exist to read it from.
   */
  onSetAttachment?: (attachmentId: string, config: unknown | null, pluginName: string) => void;
  /**
   * Optional control rendered at the start of the row, before the applied
   * chips (e.g. the "+ Add context" trigger). When present, the row stays
   * visible even with no applied context so the trigger is a permanent entry
   * point.
   */
  leading?: ReactNode;
  /**
   * Optional per-attachment controls rendered inside each attachment chip, just
   * before its remove button, keyed by attachment id (e.g. the "no blueprint
   * for this repo" nudge tucked next to the repo chip's remove button).
   */
  attachmentAccessories?: Record<string, ReactNode>;
}

interface ContextChipProps {
  icon: ReactNode;
  children: ReactNode;
  removeLabel: string;
  onRemove: () => void;
  testId?: string;
  /** Optional control rendered inside the chip, just before the remove button. */
  accessory?: ReactNode;
  /**
   * Optional disclosure control rendered between the label and the accessory
   * (a chevron that opens the chip's editor).
   *
   * It is its own button rather than the chip body because a summary may itself
   * be interactive: the repo chip renders a real `<input>` for its branch, and
   * nesting that inside a trigger button would be invalid markup and would
   * steal the field's clicks.
   */
  disclosure?: ReactNode;
}

/**
 * One removable pill in the applied-context row. Animated in/out so adding or
 * removing context reads as a small, deliberate change above the composer.
 */
function ContextChip({
  icon,
  children,
  removeLabel,
  onRemove,
  testId,
  accessory,
  disclosure,
}: ContextChipProps) {
  return (
    <motion.span
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.12 }}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card/60 py-1 pl-2 pr-1 text-2xs"
    >
      {icon}
      {/*
        A flex row, not a single `truncate` block. A summary can contain an
        atomic inline box (the repo chip's editable branch field is an
        `<input>`), and `text-overflow: ellipsis` drops such a box whole rather
        than clipping it — the branch vanished entirely, leaving blank space
        inside the chip, as soon as `owner/repo` plus the branch overflowed the
        cap. Flex lets each part shrink and ellipsize on its own instead.
      */}
      <span className="flex min-w-0 max-w-[16rem] items-center overflow-hidden">{children}</span>
      {disclosure}
      {accessory}
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={removeLabel}
        onClick={onRemove}
        className="size-4 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
      >
        <X className="size-3" />
      </Button>
    </motion.span>
  );
}

interface AttachmentContextChipProps {
  definition: ResolvedAttachment;
  /**
   * Undefined while the chip is being held open after its last value was
   * removed from inside the picker (see `AppliedContextBar`).
   */
  value: PluginAttachmentValue | undefined;
  onRemove: () => void;
  onChange?: (config: unknown) => void;
  accessory?: ReactNode;
  /** Writes config from the chip's own picker. Absent means no disclosure. */
  onSetConfig?: (config: unknown | null) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Renders an applied attachment as a chip, reusing the plugin's own
 * `summaryComponent` (e.g. the repo slug, "GitHub +1" for MCP servers) so the
 * glanceable label matches the advanced attachment chips. Falls back to the
 * attachment label when the plugin ships no summary component.
 *
 * When `onChange` is given it is handed to the summary, letting it edit part of
 * the config in place; clearing the attachment stays the chip's remove button.
 */
function AttachmentContextChip({
  definition,
  value,
  onRemove,
  onChange,
  accessory,
  onSetConfig,
  open,
  onOpenChange,
}: AttachmentContextChipProps) {
  const Icon = definition.icon;
  const pickerProviders = useAttachmentPickerProviders();
  const label = definition.label;

  return (
    <ContextChip
      icon={
        summaryOwnsIcon(definition) ? null : <Icon className="icon-xs shrink-0 text-foreground" />
      }
      removeLabel={`Remove ${label}`}
      onRemove={onRemove}
      testId={`applied-context-chip-${definition.id}`}
      accessory={accessory}
      disclosure={
        onSetConfig ? (
          <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Edit ${label}`}
                data-testid={`applied-context-disclosure-${definition.id}`}
                className="size-4 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
              >
                <ChevronDown className="size-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              aria-label={label}
              // Without a real cap an org with several servers renders every
              // pill on one line and the popover spans the viewport.
              className={`w-auto min-w-[14rem] ${popoverBoundedMaxWidthClass('22rem')} p-2`}
              // The pickers open hover-card-backed pills; letting Radix
              // autofocus the first one would pop its hover card open the
              // instant the popover appears.
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <AttachmentPickerPanel
                definition={definition}
                config={value?.config ?? null}
                onChange={onSetConfig}
                {...pickerProviders}
              />
            </PopoverContent>
          </Popover>
        ) : undefined
      }
    >
      {value ? (
        <AttachmentSummaryLabel
          definition={definition}
          config={value.config ?? null}
          onChange={onChange}
          fallback={<span className="truncate">{label}</span>}
        />
      ) : (
        // Held open with nothing attached: the summary would read "Added",
        // which is worse than naming the kind the picker is editing.
        <span className="truncate">{label}</span>
      )}
    </ContextChip>
  );
}

/**
 * Animated row of removable chips for context applied to the launch run via the
 * `@`-mention menu (or the advanced config below): the active persona plus any
 * plugin attachments. This row gives the just-mentioned context an immediate,
 * glanceable home directly above the composer. Renders nothing (and collapses
 * its height) when there is no applied context.
 *
 * In the launch dialog it *replaces* the advanced attachment chips rather than
 * complementing them (`AgentConfigFormSection` is mounted with
 * `hideAttachments`), so each attachment chip has to be a management surface
 * and not just a label: `onSetAttachment` gives it a disclosure hosting the
 * plugin's own picker. Without that, a multi-value attachment could only be
 * cleared wholesale. Elsewhere (the Persona editor, the Flow panel) the grid is
 * still rendered and this row is not.
 */
export function AppliedContextBar({
  attachmentValues,
  activePersona,
  activeGroup,
  references = [],
  onClearPersona,
  onClearGroup,
  onRemoveAttachment,
  onChangeAttachment,
  onRemoveReference,
  onSetAttachment,
  leading,
  attachmentAccessories,
}: AppliedContextBarProps) {
  const { attachments } = usePlugins();
  // The chip whose picker popover is open, if any. Tracked here because
  // deselecting an attachment's last item clears the whole attachment, which
  // would drop the chip — and with it the popover the user is still working in.
  // Holding it mounted until the popover closes keeps that interaction alive,
  // so removing the last MCP server and picking a different one is one visit.
  const [openAttachmentId, setOpenAttachmentId] = useState<string | null>(null);
  // `attachments` is already sorted by order/label, giving the chips a stable
  // left-to-right order regardless of the order things were applied.
  const applied = attachments.filter(
    (def) => attachmentValues[def.id] !== undefined || def.id === openAttachmentId,
  );
  const hasChips =
    Boolean(activePersona) || Boolean(activeGroup) || applied.length > 0 || references.length > 0;
  // The row stays mounted when a `leading` trigger is present so it never
  // collapses away — the trigger is always reachable, chips just fill in beside it.
  const hasContent = hasChips || Boolean(leading);

  return (
    <AnimatePresence initial={false}>
      {hasContent && (
        <motion.div
          key="applied-context-bar"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.15 }}
          className="overflow-hidden"
          data-testid="applied-context-bar"
        >
          <div className="flex flex-wrap items-center gap-1.5 pb-2">
            {leading}
            <AnimatePresence initial={false}>
              {activePersona && (
                <ContextChip
                  key="persona"
                  icon={<UserCircle className="icon-xs shrink-0 text-foreground" />}
                  removeLabel="Remove persona"
                  onRemove={onClearPersona}
                  testId="applied-context-chip-persona"
                >
                  <span className="truncate">{activePersona.name}</span>
                </ContextChip>
              )}
              {activeGroup && onClearGroup && (
                <ContextChip
                  key="group"
                  icon={<Folder className="icon-xs shrink-0 text-foreground" />}
                  removeLabel="Remove group"
                  onRemove={onClearGroup}
                  testId="applied-context-chip-group"
                >
                  <span className="truncate">{activeGroup.name}</span>
                </ContextChip>
              )}
              {applied.map((def) => (
                <AttachmentContextChip
                  key={def.id}
                  definition={def}
                  value={attachmentValues[def.id]}
                  onRemove={() => {
                    setOpenAttachmentId((id) => (id === def.id ? null : id));
                    onRemoveAttachment(def.id);
                  }}
                  onChange={
                    onChangeAttachment ? (config) => onChangeAttachment(def.id, config) : undefined
                  }
                  accessory={attachmentAccessories?.[def.id]}
                  onSetConfig={
                    onSetAttachment
                      ? (config) => onSetAttachment(def.id, config, def.pluginName)
                      : undefined
                  }
                  open={openAttachmentId === def.id}
                  onOpenChange={(next) => setOpenAttachmentId(next ? def.id : null)}
                />
              ))}
              {references.map((ref) => (
                <motion.span
                  key={`ref-${ref.id}`}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.12 }}
                >
                  <AgentRunBadge
                    agentId={ref.id}
                    title={ref.title}
                    onRemove={onRemoveReference ? () => onRemoveReference(ref.id) : undefined}
                  />
                </motion.span>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
